/**
 * The label an assistant sees against a trip.
 *
 * Only the booking's own status can say a trip is finished. A date that has
 * passed says the trip *ran*, which is not the same thing — labelling an
 * unconfirmed booking "Completed" told her a trip was closed and paid while
 * her fee was in fact still locked in her wallet, waiting on confirmations
 * that never came.
 */

const parseDateValue = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Whether the start handshake has gone through, so the trip is under way or
 * over. Such a trip is no longer "upcoming" whatever its calendar date says —
 * a trip started early, or one still running today, would otherwise sit in the
 * assistant's Upcoming Trips count until its arrival date passed.
 */
export const hasTripStarted = (rawStatus) =>
  ["trip_started", "completed"].includes(String(rawStatus || "").trim().toLowerCase());

export const mapTripStatus = (rawStatus, startValue, endValue) => {
  const normalized = String(rawStatus || "").trim().toLowerCase();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dayOf = (value) => {
    const parsed = parseDateValue(value);
    if (!parsed) return null;
    const day = new Date(parsed);
    day.setHours(0, 0, 0, 0);
    return day.getTime();
  };

  if (["rejected", "cancelled", "canceled", "failed_payment"].includes(normalized)) {
    return "Cancelled";
  }

  if (normalized === "completed") {
    return "Completed";
  }

  if (
    ["pending_assistant_action", "pending", "awaiting_confirmation", "awaiting-confirmation"].includes(
      normalized
    )
  ) {
    return "Pending";
  }

  if (
    ["assistant_arrived", "trip_started", "active", "ongoing", "in_progress"].includes(
      normalized
    )
  ) {
    return "Active";
  }

  if (["accepted", "confirmed"].includes(normalized)) {
    const start = dayOf(startValue);
    if (start === null) return "Upcoming";
    if (start > today.getTime()) return "Upcoming";
    // A trip occupies every day through departure, so it is still running
    // until that date passes.
    const end = dayOf(endValue) ?? start;
    if (end >= today.getTime()) return "Active";
    return "Unconfirmed";
  }

  return "Upcoming";
};
